import { Injectable, Renderer2, DOCUMENT, inject } from '@angular/core';
import type { ICultureResources, Head, MetaTag, ResourceConfig } from '../models';
import { cultureResources, defaultCulture, globalResources } from '../resources';
import { ResourceService } from './resource.service';
import { environment } from './../../environments';

@Injectable({ providedIn: 'root' })
export class HeadService {
    private _document = inject<Document>(DOCUMENT);
    private _resourceService = inject(ResourceService);

    init(
        _renderer: Renderer2,
        _head: Head,
        _culture: string,
        _isInStoryblokPreview = false,
        _structuredDataScript?: string | null,
    ): void {
        this._resourceService.clearNonPersistentElements(_renderer);
        this._applyStatic(_renderer, _head, _culture);
        this._applyStructuredData(_renderer, _structuredDataScript);
        this._applyDynamic(_renderer, _culture, _isInStoryblokPreview);
    }

    private _applyStatic(renderer: Renderer2, head: Head, culture: string): void {
        const [htmlElement, canonicalLink] = [
            this._document.querySelector('html'),
            this._document.querySelector('link[rel="canonical"]'),
        ];

        renderer.setProperty(htmlElement, 'lang', culture ?? 'en');
        renderer.setProperty(this._document, 'title', head.title ?? '');
        renderer.setProperty(canonicalLink, 'href', head.canonicalUrl ?? '');

        this.setMetaTagsFromPageModel(renderer, head.meta);
    }

    private _applyStructuredData(renderer: Renderer2, json: string | null | undefined): void {
        const existing = this._document.getElementById('structured-data');
        if (existing?.parentNode) {
            renderer.removeChild(existing.parentNode, existing);
        }

        const text = json?.trim().replace(/</g, '\\u003C');
        if (!text) {
            return;
        }

        const script = renderer.createElement('script');
        renderer.setAttribute(script, 'id', 'structured-data');
        renderer.setAttribute(script, 'type', 'application/ld+json');
        renderer.setProperty(script, 'textContent', text);
        renderer.setAttribute(script, 'data-injected', 'true');
        renderer.setAttribute(script, 'data-persist', 'false');
        renderer.appendChild(this._document.head, script);
    }

    private _applyDynamic(renderer: Renderer2, culture: string, isInStoryblokPreview: boolean): void {
        const cultureCfg: ICultureResources = cultureResources[culture] || defaultCulture;

        const validationErrors = this._resourceService.validate(cultureCfg);
        if (validationErrors.length > 0) {
            console.error('Invalid culture resources:', validationErrors);
            throw new Error('Culture resource validation failed.');
        }

        const cssVarsContent = Object.entries(cultureCfg.cssVars)
            .map(([key, value]) => `--sir-${this.camelToKebab(key)}: "${value}";`)
            .join(' ');

        const cssVars: ResourceConfig = {
            tag: 'style',
            location: 'head',
            attributes: { id: 'css-vars' },
            textContent: `:root { ${cssVarsContent} }`,
            persist: true,
        };

        const all: ResourceConfig[] = [
            cssVars,
            ...globalResources,
            ...(cultureCfg.preloads || defaultCulture.preloads || []),
            ...(cultureCfg.styles || defaultCulture.styles),
            ...(cultureCfg.scripts || defaultCulture.scripts || []),
            ...(cultureCfg.noscripts || defaultCulture.noscripts || []),
            ...(cultureCfg.meta || defaultCulture.meta || []),
        ];
        const filtered = all.filter((resource) => this._isResourceAllowed(resource, culture, isInStoryblokPreview));

        this._resourceService.inject(
            renderer,
            filtered.filter((res) => res.location === 'head'),
            '[data-resource-target="head-resources"]',
            { prettyPrint: environment.production === false }
        );
        this._resourceService.inject(
            renderer,
            filtered.filter((res) => res.location === 'body'),
            '[data-resource-target="body-resources"]',
            { prettyPrint: environment.production === false }
        );
    }

    private setMetaTagsFromPageModel(renderer: Renderer2, metaGroups: MetaTag[][]): void {
        metaGroups.forEach((group) => {
            const meta = renderer.createElement('meta');
            group.forEach((attr) => renderer.setAttribute(meta, attr.name, attr.value));

            renderer.appendChild(this._document.head, meta);
        });
    }

    private camelToKebab(str: string): string {
        return str.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
    }

    private _isResourceAllowed(resource: ResourceConfig, culture: string, isInStoryblokPreview: boolean): boolean {
        const { policy } = resource;
        if (!policy) return true;

        if (policy.excludeCultures?.includes(culture)) {
            return false;
        }

        if (isInStoryblokPreview && policy.excludeInStoryblokPreview) {
            return false;
        }

        return true;
    }
}
